import { Chip, CircularProgress, InputAdornment } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import SearchIcon from '@material-ui/icons/Search';
import { useCallback, useEffect, useState } from 'react';
import { Form } from 'react-final-form';
import { Mentor, MentorId } from '../../api/mentors';
import venturesAPI from '../../api/ventures';
import { Venture } from '../../api/ventures/types/Venture';
import { useResourceBundles } from '../../contexts/resource-bundles-context';
import { isMobile, removeDuplicates } from '../../utils/functions';
import VentureAutocomplete from '../autocomplete/venture-autocomplete';
import {
  AssignmentRecommendedTable,
  Button,
  FormButtons,
  FormGroup,
  TextField,
} from '../common';

interface RecommendedVenture extends Venture {
  ai?: boolean;
}

interface AssignMentorsFormProps {
  mentorId: Mentor['id'];
  ventures?: Venture[];
  loading?: boolean;
  onLoaded?: (ventures: Venture[]) => any;
  onSuccess: (params: {
    assigned: string[];
    removed: string[];
    all: Venture[];
  }) => any;
  onCancel: () => any;
}

const useStyles = makeStyles((theme) => ({
  form: {
    display: 'flex',
    flexDirection: 'column',
    width: '100%',
    minHeight: 285,
    height: '100%',
  },
  formButtons: {
    alignItems: 'flex-end',
    flexGrow: 1,
  },
  checkboxGroup: {
    maxHeight: 360,
    overflow: 'auto',
  },
  loader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexGrow: 1,
  },
  chips: {
    display: 'flex',
    alignItems: 'center',
    flexFlow: 'row wrap',
    margin: '15px 0 0 -10px',
  },
  chipItem: {
    margin: '0 0 10px 10px',
    overflow: 'hidden',

    [theme.breakpoints.up('sm')]: {
      overflow: 'initial',
    },
  },
}));

function AssignMentorsForm({
  mentorId,
  ventures,
  loading = false,
  onLoaded = () => {},
  onSuccess,
  onCancel,
}: AssignMentorsFormProps) {
  const classes = useStyles();
  const { rb } = useResourceBundles();

  const [loadedVentures, setLoadedVentures] = useState<Venture[]>(
    ventures || [],
  );
  const [initialVentures, setInitialVentures] =
    useState<Venture[]>(loadedVentures);
  const [displayedVentures, setDisplayedVentures] = useState<
    RecommendedVenture[]
  >([]);
  const [recommended, setRecommended] = useState<RecommendedVenture[]>();
  const [isLoading, setIsLoading] = useState<{
    ventures: boolean;
    recommended: boolean;
  }>({ ventures: !ventures, recommended: !recommended });
  const [searchIteration, setSearchIteration] = useState(0);

  const handleVenturesSearch = useCallback((searchedVentures: Venture[]) => {
    setDisplayedVentures(searchedVentures);
  }, []);

  const handleVentureRemove = (venture: Venture) => {
    setInitialVentures((prevVentures) =>
      prevVentures.filter((prevVenture) => prevVenture.id !== venture.id),
    );
  };

  const handleVentureAdd = (venture: Venture) => {
    setInitialVentures((prevVenture) => [...prevVenture, venture]);
    setSearchIteration((prevIteration) => prevIteration + 1);
  };

  const loadInitialVentures = async (_mentorId: MentorId) => {
    try {
      const loadedVentures = await venturesAPI.getVenturesDetailsByMentorId(
        _mentorId,
      );
      setLoadedVentures(loadedVentures);
      setInitialVentures(loadedVentures);
      onLoaded(loadedVentures);
      setIsLoading((prevLoading) => ({
        ...prevLoading,
        ventures: false,
      }));
    } catch (e: any) {
      setIsLoading((prevLoading) => ({
        ...prevLoading,
        ventures: false,
      }));
    }
  };

  const handleSubmit = async ({
    venturesIds,
  }: {
    venturesIds: Venture['id'][];
  }) => {
    const loadedVenturesIds = loadedVentures.map((venture) => venture.id);
    const assigned = venturesIds.filter(
      (id) => !loadedVenturesIds.includes(id),
    );
    const removed = loadedVenturesIds.filter((id) => !venturesIds.includes(id));
    const all = initialVentures.filter((venture) =>
      venturesIds.includes(venture.id),
    );
    onSuccess({ assigned, removed, all });
  };

  useEffect(() => {
    if (!ventures) {
      loadInitialVentures(mentorId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ventures]);

  useEffect(() => {
    const loadRecommended = async () => {
      try {
        let i = 0;
        let loaded: Venture[] = [];
        const requests = [
          () => venturesAPI.getVenturesRecommendedByAIForMentor(mentorId),
          () => venturesAPI.getVenturesByAssignment('notassigned', 0),
          () => venturesAPI.getVenturesByAssignment('1', 0),
          () => venturesAPI.getVenturesByAssignment('2', 0),
          () => venturesAPI.getVenturesByAssignment('morethanthree', 0),
        ];
        while (i < requests.length) {
          try {
            const entities = await requests[i]();
            const procEntities =
              i === 0
                ? entities.map((entity) => ({ ...entity, ai: true }))
                : entities;
            loaded = [...loaded, ...procEntities];
          } catch (e: any) {}
          if (loaded.length >= 10) {
            i = requests.length;
          } else {
            i++;
          }
        }
        const loadedWithoutDuplicates = removeDuplicates(loaded);
        setRecommended(loadedWithoutDuplicates);
        setIsLoading((prevLoading) => ({
          ...prevLoading,
          recommended: false,
        }));
      } catch (e: any) {
        console.log('error loadRecommended');
        setIsLoading((prevLoading) => ({
          ...prevLoading,
          recommended: false,
        }));
      }
    };

    loadRecommended();
  }, [mentorId]);

  if (isLoading.ventures || isLoading.recommended) {
    return (
      <div className={classes.form}>
        <div className={classes.loader}>
          <CircularProgress size={36} color='primary' />
        </div>
        <FormButtons>
          <Button onClick={onCancel} variant='outlined'>
            Cancel
          </Button>
          <Button disabled>Apply</Button>
        </FormButtons>
      </div>
    );
  }

  const initialValues = {
    venturesIds: initialVentures.map((venture) => venture.id),
  };

  const filteredDisplayedVentures = displayedVentures.filter(
    (venture) =>
      !initialVentures.find(
        (initialVenture) => initialVenture.id === venture.id,
      ),
  );
  const filteredRecommendedVentures = (recommended || []).filter(
    (item) => !initialVentures.find((venture) => venture.id === item.id),
  );

  const displayedList =
    filteredDisplayedVentures.length > 0
      ? filteredDisplayedVentures
      : filteredRecommendedVentures;

  return (
    <Form
      initialValues={initialValues}
      onSubmit={handleSubmit}
      keepDirtyOnReinitialize
      render={(formProps) => (
        <form
          data-testid='form-mentor-assignment'
          className={classes.form}
          onSubmit={formProps.handleSubmit}
          noValidate>
          <FormGroup>
            <VentureAutocomplete
              iteration={searchIteration}
              onSearch={handleVenturesSearch}
              inputRenderer={({ value, onChange, isLoading }) => (
                <TextField
                  value={value}
                  label='Assign to'
                  placeholder='Enter venture name'
                  onChange={onChange}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position='start'>
                        <SearchIcon />
                      </InputAdornment>
                    ),
                    endAdornment: !!isLoading && (
                      <CircularProgress color='primary' size={20} />
                    ),
                  }}
                />
              )}
            />
          </FormGroup>
          <div className={classes.chips}>
            {initialVentures.map((venture) => (
              <Chip
                key={venture.id}
                label={venture.ventureName}
                onDelete={() => handleVentureRemove(venture)}
                className={classes.chipItem}
              />
            ))}
          </div>
          {displayedList.length > 0 && (
            <AssignmentRecommendedTable
              labels={[
                'Ventures',
                isMobile() ? 'Assigned' : `Assigned ${rb('mentors-u')}`,
              ]}
              rows={displayedList.map((recomItem) => ({
                name: recomItem.ventureName,
                assigned: recomItem.assignedMentors,
                ai: recomItem.ai,
                onAdd: () => handleVentureAdd(recomItem),
              }))}
              mobileFullHeight
            />
          )}
          <FormButtons className={classes.formButtons}>
            <Button
              onClick={onCancel}
              variant='outlined'
              data-testid='mentor-assignment-cancel'>
              Cancel
            </Button>
            <Button
              disabled={loading}
              type='submit'
              data-testid='mentor-assignment-apply'>
              {loading ? (
                <CircularProgress size={24} color='inherit' />
              ) : (
                'Apply'
              )}
            </Button>
          </FormButtons>
        </form>
      )}
    />
  );
}

export default AssignMentorsForm;
